import * as React from "react";
import { addArtNoteToOrderMutation } from "../../graph";
import { addNoteToOrderProductionJobCustomizationAreaMutation } from "../../graph";
import { addProductionNoteToOrderMutation } from "../../graph";
import { makeStyles } from "@mui/styles";
import { useMutation } from "@apollo/client";
import { useState } from "react";
import Button from "@mui/material/Button";
import CenteredSpinner from "../../components/CenteredSpinner";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";

const defaultInputErrors = {
  note: [],
  orderId: [],
  orderProductionJobCustomizationAreaId: []
};

const useStyles = makeStyles(theme => ({
  formControl: {
    marginBottom: theme.spacing(1),
    minWidth: 120
  },

  formAction: {
    alignItems: "flex-end",
    display: "flex",
    marginBottom: theme.spacing(1)
  },

  addNoteContainer: {
    padding: theme.spacing(2)
  }
}));

const AddNoteForm = ({
  customizationAreaId,
  isAddingArtNote,
  isAddingCustomizationAreaNote,
  order,
  placeholder
}) => {
  const classes = useStyles();
  const id = order?.id;

  // States:
  const [inputErrors, setInputErrors] = useState(defaultInputErrors);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteErrors, setNoteErrors] = useState(false);
  const [noteTextAreaValue, setNoteTextAreaValue] = useState("");

  // Mutations:
  const [addArtNoteToOrder] = useMutation(addArtNoteToOrderMutation);
  const [addNoteToOrderProductionJobCustomizationArea] = useMutation(
    addNoteToOrderProductionJobCustomizationAreaMutation
  );
  const [addProductionNoteToOrder] = useMutation(
    addProductionNoteToOrderMutation
  );

  // Handlers:
  const handleArtFormSubmit = event => {
    event.preventDefault();
    const requiredInputErrors = {};
    if (!noteTextAreaValue) {
      setNoteErrors(true);
      requiredInputErrors.note = ["is required"];
    }

    if (Object.keys(requiredInputErrors).length > 0) {
      setNoteErrors(true);
      setInputErrors({
        ...defaultInputErrors,
        ...requiredInputErrors
      });
      return;
    }

    setIsAddingNote(true);
    addArtNoteToOrder({
      variables: { orderId: id, note: noteTextAreaValue }
    })
      .then(
        ({
          data: {
            addArtNoteToOrder: { succeeded, errors }
          }
        }) => {
          if (!succeeded) {
            setNoteErrors(true);
            setInputErrors(...errors);
          } else {
            setNoteTextAreaValue("");
            setNoteErrors(false);
            setInputErrors("");
          }
        }
      )
      .finally(() => {
        setIsAddingNote(false);
      });
  };

  const handleFormSubmit = event => {
    event.preventDefault();
    const requiredInputErrors = {};

    if (!noteTextAreaValue) {
      setNoteErrors(true);
      requiredInputErrors.note = ["is required"];
    }

    if (Object.keys(requiredInputErrors).length > 0) {
      setNoteErrors(true);
      setInputErrors({
        ...defaultInputErrors,
        ...requiredInputErrors
      });
      return;
    }
    setIsAddingNote(true);
    addProductionNoteToOrder({
      variables: { orderId: id, note: noteTextAreaValue }
    })
      .then(
        ({
          data: {
            addProductionNoteToOrder: { succeeded, errors }
          }
        }) => {
          if (!succeeded) {
            setNoteErrors(true);
            setInputErrors(...errors);
          } else {
            setNoteTextAreaValue("");
            setNoteErrors(false);
            setInputErrors("");
          }
        }
      )
      .finally(() => {
        setIsAddingNote(false);
      });
  };

  const handleCustomizationAreaNoteFormSubmit = event => {
    event.preventDefault();

    const requiredInputErrors = {};
    if (!noteTextAreaValue) {
      setNoteErrors(true);
      requiredInputErrors.note = ["is required"];
    }

    if (Object.keys(requiredInputErrors).length > 0) {
      setNoteErrors(true);
      setInputErrors({
        ...defaultInputErrors,
        ...requiredInputErrors
      });
      return;
    }
    setIsAddingNote(true);

    addNoteToOrderProductionJobCustomizationArea({
      variables: {
        orderProductionJobCustomizationAreaId: customizationAreaId,
        note: noteTextAreaValue
      }
    })
      .then(
        ({
          data: {
            addNoteToOrderProductionJobCustomizationArea: { succeeded, errors }
          }
        }) => {
          if (!succeeded) {
            setNoteErrors(true);
            setInputErrors(...errors);
          } else {
            setNoteTextAreaValue("");
            setNoteErrors(false);
            setInputErrors("");
          }
        }
      )
      .finally(() => {
        setIsAddingNote(false);
      });
  };

  const handleNoteTextAreaChange = event => {
    setNoteTextAreaValue(event.target.value);
  };

  return (
    <div className={classes.addNoteContainer}>
      <form>
        <FormControl className={classes.formControl} fullWidth={true}>
          <TextField
            required
            helperText={noteErrors && inputErrors.note}
            error={noteErrors}
            fullWidth={true}
            placeholder={placeholder}
            onChange={handleNoteTextAreaChange}
            multiline={true}
            variant="outlined"
            value={noteTextAreaValue}
          />
        </FormControl>
        <div>
          {isAddingNote ? (
            <CenteredSpinner />
          ) : (
            <FormControl className={classes.formAction}>
              <Button
                color="primary"
                onClick={
                  isAddingCustomizationAreaNote
                    ? handleCustomizationAreaNoteFormSubmit
                    : isAddingArtNote
                      ? handleArtFormSubmit
                      : handleFormSubmit
                }
                variant="contained"
              >
                Add Note
              </Button>
            </FormControl>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddNoteForm;
